import { c as _c } from "react/compiler-runtime";
import { AccessibilitySolid, ArrowUpRight, BedSolid, ClockSolid, QuestionMark, SecuritySolid, ThumbsUpSolid, TrainSolid } from '@byway/icons';
import type { IconProps } from '@byway/icons/src/icons/Icon.types';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import type { ComponentType, ReactNode } from 'react';
import { Heading } from 'ui';
import { Button } from '~/components/Button';
import { Divider } from '~/components/Divider';
import { LoyaltyBenefitsSection, LoyaltyPriceDisplay } from '~/components/Journey/Loyalty';
import { useCurrencyContext } from '~/contexts/Currencies';
import { useLoyaltyPricing } from '~/hooks/useLoyaltyPricing';
import { PERCENT_DEPOSIT } from '~/types/Payment.types';
import { formatGBP } from '~/utils/currencyHelpers';
import { Link } from '../../Link';
import styles from './PriceDetails.module.css';
type IconLabelItem = {
  icon: ComponentType<IconProps>;
  label: string;
};
type PriceDetailsProps = {
  paxCount: number;
  roomCount: number;
  offerDeposit?: boolean;
  labelItem?: IconLabelItem[];
  price: {
    total: {
      value: number;
      formatted: string;
    };
    perPerson: {
      value: number;
      formatted: string;
    };
  };
  onClose?: () => void;
};
function PriceDetails(t0) {
  const $ = _c(90);
  const {
    paxCount,
    roomCount,
    labelItem,
    onClose,
    price,
    offerDeposit: t1
  } = t0;
  const offerDeposit = t1 === undefined ? false : t1;
  const t = useTranslations("journey");
  const tCommon = useTranslations("common");
  const {
    hasLoyaltyDiscount,
    discountedTotal
  } = useLoyaltyPricing();
  const pricingLearnMoreHref = hasLoyaltyDiscount ? "/account/rewards" : "https://www.byway.travel/journal/byway-trip-prices";
  let t2;
  if ($[0] !== t) {
    t2 = t("includes_transport");
    $[0] = t;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== t2) {
    t3 = {
      icon: TrainSolid,
      label: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("includes_accommodation");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = {
      icon: BedSolid,
      label: t4
    };
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = t("includes_package_protection");
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = {
      icon: SecuritySolid,
      label: t6
    };
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t) {
    t8 = t("includes_disruption_guarantee");
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = {
      icon: ThumbsUpSolid,
      label: t8
    };
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t) {
    t10 = t("includes_support");
    $[16] = t;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10) {
    t11 = {
      icon: ClockSolid,
      label: t10
    };
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t) {
    t12 = t("includes_accessibility");
    $[20] = t;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12) {
    t13 = {
      icon: AccessibilitySolid,
      label: t12
    };
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t11 || $[25] !== t13 || $[26] !== t3 || $[27] !== t5 || $[28] !== t7 || $[29] !== t9) {
    t14 = [t3, t5, t7, t9, t11, t13];
    $[24] = t11;
    $[25] = t13;
    $[26] = t3;
    $[27] = t5;
    $[28] = t7;
    $[29] = t9;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  const includedItems = t14;
  const {
    convertPriceInGBP
  } = useCurrencyContext();
  let t15;
  if ($[31] !== convertPriceInGBP || $[32] !== discountedTotal || $[33] !== hasLoyaltyDiscount || $[34] !== price.total.value) {
    const depositAmount = Math.round((hasLoyaltyDiscount ? discountedTotal : price.total.value) * (PERCENT_DEPOSIT / 100));
    t15 = convertPriceInGBP(depositAmount) || formatGBP(depositAmount);
    $[31] = convertPriceInGBP;
    $[32] = discountedTotal;
    $[33] = hasLoyaltyDiscount;
    $[34] = price.total.value;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  const depositFormatted = t15;
  const totalPriceFormatted = price.total.formatted;
  const showPriceExplanation = process.env.NEXT_PUBLIC_PRICE_EXPLANATION_ENABLED === "true";
  const paxPerRoom = Math.ceil(paxCount / Math.max(roomCount, 1));
  let t16;
  if ($[36] !== t) {
    t16 = t("includes_heading");
    $[36] = t;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t16) {
    t17 = <Heading as="h2" variant="h6">{t16}</Heading>;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== includedItems) {
    t18 = <ul className={styles.includedList}>{includedItems.map(_temp)}</ul>;
    $[40] = includedItems;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t17 || $[43] !== t18) {
    t19 = <div className={styles.includedSection}>{t17}{t18}</div>;
    $[42] = t17;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== labelItem || $[46] !== t) {
    t20 = labelItem && labelItem.length > 0 && <div className={styles.tripDetailsSection}><Divider variant="horizontal" /><Heading as="h2" variant="h6">{t("price_details_trip_details_heading")}</Heading>{labelItem.map(_temp2)}</div>;
    $[45] = labelItem;
    $[46] = t;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className={styles.divider}><Divider variant="horizontal" /></div>;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = clsx(styles.pricingNote, showPriceExplanation && styles.pricingNoteHasFromPrice);
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <div className={styles.questionMarkCircle}><QuestionMark decorative={true} size="large" strokeWidth={2} /></div>;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== paxCount || $[52] !== paxPerRoom || $[53] !== roomCount || $[54] !== t) {
    t24 = showPriceExplanation && <p className={styles.pricingNoteText}>{paxPerRoom <= 1 ? t("price_details_pricing_note_solo") : t("price_details_pricing_note_sharing", {
        person_count: paxCount,
        room_count: roomCount
      })}</p>;
    $[51] = paxCount;
    $[52] = paxPerRoom;
    $[53] = roomCount;
    $[54] = t;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== hasLoyaltyDiscount) {
    t25 = hasLoyaltyDiscount ? {} : {
      target: "_blank"
    };
    $[56] = hasLoyaltyDiscount;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t) {
    t26 = t("price_details_learn_more");
    $[58] = t;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== hasLoyaltyDiscount) {
    t27 = !hasLoyaltyDiscount && <ArrowUpRight decorative={true} strokeWidth={2} />;
    $[60] = hasLoyaltyDiscount;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t26 || $[63] !== t27) {
    t28 = <span className={styles.pricingLink}>{t26} {t27}</span>;
    $[62] = t26;
    $[63] = t27;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  let t29;
  if ($[65] !== pricingLearnMoreHref || $[66] !== t25 || $[67] !== t28) {
    t29 = <Link hasUnderline={true} href={pricingLearnMoreHref} {...t25}>{t28}</Link>;
    $[65] = pricingLearnMoreHref;
    $[66] = t25;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  let t30;
  if ($[69] !== t24 || $[70] !== t29) {
    t30 = <div className={styles.pricingNoteSection}><div className={t22}>{t23}<div className={styles.pricingNoteContent}>{t24}{t29}</div></div></div>;
    $[69] = t24;
    $[70] = t29;
    $[71] = t30;
  } else {
    t30 = $[71];
  }
  let t31;
  if ($[72] !== hasLoyaltyDiscount) {
    t31 = hasLoyaltyDiscount && <div className={styles.loyaltyBenefitsSection}><div className={styles.loyaltyBenefitsDivider}><Divider variant="horizontal" /></div><LoyaltyBenefitsSection /></div>;
    $[72] = hasLoyaltyDiscount;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== depositFormatted || $[75] !== hasLoyaltyDiscount || $[76] !== offerDeposit || $[77] !== t || $[78] !== totalPriceFormatted) {
    t32 = totalPriceFormatted && <div className={styles.totalSection}><div className={styles.totalDivider}><Divider variant="horizontal" /></div><div className={styles.totalPrice}><Heading as="h2" variant="h6">{t("price_details_total")}</Heading>{hasLoyaltyDiscount ? <LoyaltyPriceDisplay variant="total" /> : <span className={styles.totalPriceValue}>{totalPriceFormatted}</span>}</div>{offerDeposit && depositFormatted && <p className={styles.depositBadge}>{t("price_details_deposit_note", {
          percent: PERCENT_DEPOSIT
        })} <strong>{depositFormatted}</strong></p>}</div>;
    $[74] = depositFormatted;
    $[75] = hasLoyaltyDiscount;
    $[76] = offerDeposit;
    $[77] = t;
    $[78] = totalPriceFormatted;
    $[79] = t32;
  } else {
    t32 = $[79];
  }
  let t33;
  if ($[80] !== onClose || $[81] !== tCommon) {
    t33 = onClose && <div className={styles.footer}><Button size="fullWidth" onPress={onClose} dataTestId="price-details-okay">{tCommon("okay")}</Button></div>;
    $[80] = onClose;
    $[81] = tCommon;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== t19 || $[84] !== t20 || $[85] !== t30 || $[86] !== t31 || $[87] !== t32 || $[88] !== t33) {
    t34 = <div className={styles.content}>{t19}{t20}{t21}{t30}{t31}{t32}{t33}</div>;
    $[83] = t19;
    $[84] = t20;
    $[85] = t30;
    $[86] = t31;
    $[87] = t32;
    $[88] = t33;
    $[89] = t34;
  } else {
    t34 = $[89];
  }
  return t34;
}
function _temp2(t0) {
  const {
    icon: Icon_0,
    label: label_0
  } = t0;
  return <div key={label_0} className={styles.tripDetailItem}><CircleWrapper variant="contrast"><Icon_0 decorative={true} size="large" /></CircleWrapper><span>{label_0}</span></div>;
}
function _temp(t0) {
  const {
    icon: Icon,
    label
  } = t0;
  return <li key={label} className={styles.includedItem}><CircleWrapper><Icon decorative={true} size="large" /></CircleWrapper><span>{label}</span></li>;
}
type CircleWrapperProps = {
  children: ReactNode;
  variant?: 'default' | 'contrast';
};
function CircleWrapper(t0) {
  const $ = _c(5);
  const {
    children,
    variant: t1
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const t2 = variant === "contrast";
  let t3;
  if ($[0] !== t2) {
    t3 = clsx(styles.iconContainer, {
      [styles.contrast]: t2
    });
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== children || $[3] !== t3) {
    t4 = <div className={t3}>{children}</div>;
    $[2] = children;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}
export { PriceDetails };